import {
  AcademicCapIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ComputerDesktopIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { Feature } from 'admin/src/enums';
import { ManageScheduledClass } from 'pollworker/src/components/ModalManageScheduledClass';
import PageEmptyState from 'pollworker/src/components/PageEmptyState';
import { renderDataAttribute } from 'pollworker/src/components/WorkSchedules';
import RemoteCourseRow from 'pollworker/src/components/training/RemoteCourseRow';
import SectionJumps, { type SectionJumpGroup } from 'pollworker/src/components/training/SectionJumps';
import TrainingClassRow from 'pollworker/src/components/training/TrainingClassRow';
import TrainingSection from 'pollworker/src/components/training/TrainingSection';
import { getRemoteCourseTitle, groupTrainingClasses, hasOnlineOption } from 'pollworker/src/components/training/groupTrainingClasses';
import { dropClassForStaffing, getClassDocumentBySessionIdForStaffing, getScheduledClassDetailForStaffing, signUpForClassSessionForStaffing } from 'pollworker/src/fetchers';
import { useAppDispatch, useAppSelector, useIsFeatureFlagEnabled } from 'pollworker/src/hooks';
import { TrainingClassStatus, loadTrainingOverview, selectTrainingClasses, selectTrainingLoading } from 'pollworker/src/state/training';
import { useEffect, useMemo, useState } from 'react';
import { Flexor, Spinner } from 'shared/src/components';
import { ScreenReaderOnly } from 'shared/src/components/Accessibility';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
} from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';
import { openPdfToPrint } from 'shared/src/utils/downloadDocument';
import { toast } from 'sonner';

import dayjs from 'dayjs';
import type { ClassSession, UserTrainingClass } from 'pollworker/src/state/training';
import { apiDateConverter } from 'pollworker/src/utils/apiDateConverter';
import { useLocation } from 'wouter';

const INCOMPLETE_SECTION_ID = 'incomplete-courses';
const UNSCHEDULED_SECTION_ID = 'unscheduled';
const BOOKED_SECTION_ID = 'booked';
const COMPLETED_SECTION_ID = 'completed';

export default function MyTraining() {
  const [availableSessions, setAvailableSessions] = useState<ClassSession[]>([]);
  const [pickerClassName, setPickerClassName] = useState('');
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [showSignupComplete, setShowSignupComplete] = useState(false);
  const [showDropClassView, setShowDropClassView] = useState(false);
  const [showScheduleView, setShowScheduleView] = useState(false);
  const [managingCompletedClass, setManagingCompletedClass] = useState(false);
  const [hasSignupError, setHasSignupError] = useState(false);
  const [signingUp, setSigningUp] = useState<string>();
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [pickerSelectionId, setPickerSelectionId] = useState('');
  const [sessionIdForDownload, setSessionIdForDownload] = useState('');
  const lmsEnabled = useIsFeatureFlagEnabled(null, Feature.POLLWORKER_LMS);
  const selectedElectionId = useAppSelector((state) => state.election.electionId);
  const currentUser = useAppSelector((state) => state.currentPollworkerUser);
  const trainingClasses = useAppSelector(selectTrainingClasses) as UserTrainingClass[];
  const loading = useAppSelector(selectTrainingLoading);
  const dispatch = useAppDispatch();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!selectedElectionId) return;

    dispatch(loadTrainingOverview(selectedElectionId));
  }, [selectedElectionId, dispatch]);

  function reloadTraining() {
    if (!selectedElectionId) return;
    dispatch(loadTrainingOverview(selectedElectionId));
  }

  function closeSessionPicker() {
    setShowSessionPicker(false);
    setPickerSelectionId('');
    setHasSignupError(false);
  }

  function openSessionPicker(trainingClass: UserTrainingClass) {
    const sorted = [...(trainingClass.AvailableSessions || [])].sort((a, b) => {
      if (a.IsAfterWorkSchedule !== b.IsAfterWorkSchedule) return a.IsAfterWorkSchedule ? 1 : -1;
      return new Date(a.FirstStart).getTime() - new Date(b.FirstStart).getTime();
    });
    setAvailableSessions(sorted);
    setPickerClassName(trainingClass.ClassName);
    setPickerSelectionId('');
    setShowSessionPicker(true);
  }

  async function registerForSession(sessionId: string) {
    setSigningUp(sessionId);
    setHasSignupError(false);
    setSessionIdForDownload(sessionId);

    try {
      const success = await signUpForClassSessionForStaffing({
        CustomerId: currentUser.Customer.Id,
        EVUserId: currentUser.EVUserId,
        PWClassSessionId: sessionId,
        PollwokerId: '',
      });
      setHasSignupError(!success);
      if (success) reloadTraining();
      return success;
    } finally {
      setSigningUp(undefined);
    }
  }

  async function signUpForClass(sessionId: string) {
    const success = await registerForSession(sessionId);
    if (!success) return;

    setShowSignupComplete(true);
    closeSessionPicker();
  }

  async function startRemoteSession(session: ClassSession) {
    if (!session.Lms?.LhCourseUuid) return;

    const success = await registerForSession(session.Id);
    if (!success) return;

    closeSessionPicker();
    setLocation(`/training/courses/${encodeURIComponent(session.Lms.LhCourseUuid)}?sessionId=${encodeURIComponent(session.Id)}`);
  }

  function openManageClass(sessionId?: string, isCompleted = false) {
    if (!sessionId) return;

    setSelectedSessionId(sessionId);
    setManagingCompletedClass(isCompleted);
    setShowScheduleView(true);
  }

  async function dropClassSession(sessionId: string) {
    try {
      const success = await dropClassForStaffing(currentUser.EVUserId, sessionId);

      if (!success) {
        toast.error('An error occurred.');
        setShowDropClassView(false);
        return;
      }

      toast('You have successfully dropped this class session.');

      setShowDropClassView(false);
      setShowScheduleView(false);
      reloadTraining();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An error occurred.');
      setShowDropClassView(false);
    }
  }

  function downloadSessionDocument(sessionId: string, evUserId: string) {
    getClassDocumentBySessionIdForStaffing(sessionId, evUserId).then(openPdfToPrint);
  }

  const groupedClasses = useMemo(() => groupTrainingClasses(trainingClasses, lmsEnabled), [trainingClasses, lmsEnabled]);
  const incompleteCount = groupedClasses.incompleteRemote.length;
  const unscheduledCount = groupedClasses.unscheduledRemote.length + groupedClasses.unscheduled.length;
  const completedCount = groupedClasses.completedRemote.length + groupedClasses.completedInPerson.length;
  const outstandingCount = incompleteCount + unscheduledCount + groupedClasses.booked.length;
  const hasAnyTraining = outstandingCount + completedCount > 0;
  const outstandingSubtitle =
    outstandingCount === 0
      ? 'You are all caught up. Nothing is waiting on you right now.'
      : `You have ${outstandingCount} ${outstandingCount === 1 ? 'course' : 'courses'} left to finish before you work.`;

  const sectionJumpGroups: SectionJumpGroup[] = [
    {
      sections: [
        { id: INCOMPLETE_SECTION_ID, label: 'Incomplete Courses', count: incompleteCount },
        { id: UNSCHEDULED_SECTION_ID, label: 'Unscheduled', count: unscheduledCount },
        { id: BOOKED_SECTION_ID, label: 'Booked', count: groupedClasses.booked.length },
        { id: COMPLETED_SECTION_ID, label: 'Completed', count: completedCount },
      ],
    },
  ];

  const onlineSessions = availableSessions.filter((session) => lmsEnabled && session.Lms);
  const inPersonSessions = availableSessions.filter((session) => !lmsEnabled || !session.Lms);
  const hasBothModalities = onlineSessions.length > 0 && inPersonSessions.length > 0;
  const pickerSessions = [...onlineSessions, ...inPersonSessions];
  const selectedPickerSession = pickerSessions.find((session) => session.Id === pickerSelectionId);
  const selectedIsOnline = Boolean(selectedPickerSession?.Lms);
  const selectedIsBookable = Boolean(selectedPickerSession && !selectedPickerSession.IsAfterWorkSchedule);

  return (
    <div className="mt-2">
      <Dialog
        size="lg"
        open={showSessionPicker}
        onOpenChange={(open) => {
          if (!open) closeSessionPicker();
        }}
      >
        <DialogContent className="overflow-hidden p-0 gap-0">
          <DialogHeader>
            <DialogTitle>{hasBothModalities ? 'Choose how to complete this course' : 'Available sessions'}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-700">
              {hasBothModalities
                ? 'This course is offered online and in person. Select an option, then save your place.'
                : 'Choose a session time that works for you. Course sizes are limited, so sign up as soon as possible.'}
            </p>
            {hasSignupError ? (
              <Alert className="mt-3" variant="destructive">
                <ExclamationCircleIcon className="size-5" />
                <AlertTitle>Sign up failed</AlertTitle>
                <AlertDescription className="flex items-center justify-between gap-2">
                  <span>There was an error signing up for this session. Please try again!</span>
                  <Button size="sm" variant="ghost" onClick={() => setHasSignupError(false)}>
                    Close
                  </Button>
                </AlertDescription>
              </Alert>
            ) : null}
            {!availableSessions.length ? (
              <div className="my-10">
                <PageEmptyState
                  icon={AcademicCapIcon}
                  emptyMessageHeader="No available sessions"
                  emptyMessageSubHeader="There are no available sessions yet for this class, be sure to check back soon."
                  reload={reloadTraining}
                />
              </div>
            ) : (
              <RadioGroup className="mt-6 space-y-5 text-sm leading-6" value={pickerSelectionId} onValueChange={setPickerSelectionId}>
                {onlineSessions.length ? (
                  <div className="space-y-3">
                    {onlineSessions.map((session) => (
                      <Label
                        key={session.Id}
                        htmlFor={`session-${session.Id}`}
                        className="flex cursor-pointer items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 font-normal"
                      >
                        <RadioGroupItem id={`session-${session.Id}`} value={session.Id} className="mt-1" />
                        <div className="min-w-0 flex-1 space-y-2 text-gray-900">
                          <Flexor justify="start" items="center" className="flex-wrap gap-x-3 gap-y-1">
                            <ComputerDesktopIcon className="h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
                            <h3 className="font-semibold">{getRemoteCourseTitle(session, pickerClassName)}</h3>
                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">Self-paced</span>
                          </Flexor>
                          <p className="text-gray-700">Complete this course online at your own pace. There is no scheduled meeting time.</p>
                        </div>
                      </Label>
                    ))}
                  </div>
                ) : null}
                {hasBothModalities ? (
                  <Flexor justify="center" className="gap-3">
                    <span aria-hidden="true" className="h-px flex-auto bg-gray-200" />
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Or attend in person</span>
                    <span aria-hidden="true" className="h-px flex-auto bg-gray-200" />
                  </Flexor>
                ) : null}
                {inPersonSessions.length ? (
                  <div className="space-y-1">
                    {inPersonSessions.map((session) => (
                      <Label
                        key={session.Id}
                        htmlFor={`session-${session.Id}`}
                        className={cn(
                          'relative flex cursor-pointer space-x-3 px-3 py-3 xl:static rounded-lg border border-gray-200',
                          session.IsAfterWorkSchedule ? 'opacity-85' : 'hover:bg-gray-50',
                        )}
                      >
                        <RadioGroupItem disabled={session.IsAfterWorkSchedule} id={`session-${session.Id}`} value={session.Id} className="mt-1" />
                        <div className="flex-auto space-y-2 text-gray-900">
                          <Flexor justify="start" className="space-x-3">
                            {session.IsAfterWorkSchedule ? (
                              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
                            ) : (
                              <BuildingLibraryIcon className="h-5 w-5 shrink-0" />
                            )}
                            <h3 className="flex items-center space-x-2">{session.Location}</h3>
                          </Flexor>
                          <div className="ml-8 space-y-2">
                            <div>
                              <span className="font-semibold">{dayjs(session.FirstStart).format('dddd, MMM Do [at] hh:mm A')}</span> until{' '}
                              <span className="font-semibold">{dayjs(session.FinalEnd).format('dddd, MMM Do [at] hh:mm A')}</span>
                            </div>
                            <div>
                              <ScreenReaderOnly>Available Seats</ScreenReaderOnly>
                              {renderDataAttribute(session.SeatsAvailable)} of {renderDataAttribute(session.ClassSize)} spots available
                            </div>
                            {session.IsAfterWorkSchedule ? (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-amber-700">
                                  This session takes place after your{' '}
                                  {session.FirstWorkSchedule ? `${dayjs(apiDateConverter(session.FirstWorkSchedule)).format('MMM Do')}` : 'first work '} schedule that requires this
                                  training
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </Label>
                    ))}
                  </div>
                ) : null}
                {hasBothModalities ? (
                  <p className="text-xs text-gray-500">Once you start the online course or sign up for an in-person session, your format is set for the course.</p>
                ) : null}
              </RadioGroup>
            )}
          </DialogBody>
          {availableSessions.length ? (
            <DialogFooter>
              <Button size="sm" variant="ghost" onClick={closeSessionPicker}>
                Cancel
              </Button>
              {selectedIsOnline && selectedPickerSession ? (
                <Button size="sm" disabled={!!signingUp} onClick={() => startRemoteSession(selectedPickerSession)}>
                  {signingUp === selectedPickerSession.Id ? <Spinner light /> : null}
                  <span>Start</span>
                </Button>
              ) : (
                <Button size="sm" disabled={!!signingUp || !selectedIsBookable} onClick={() => selectedPickerSession && signUpForClass(selectedPickerSession.Id)}>
                  {signingUp && signingUp === selectedPickerSession?.Id ? <Spinner light /> : null}
                  <span>Save my place</span>
                </Button>
              )}
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog size="md" open={showSignupComplete} onOpenChange={(open) => !open && setShowSignupComplete(false)}>
        <DialogContent className="max-w-xl overflow-hidden p-0 gap-0">
          <DialogHeader>
            <DialogTitle>Congratulations!</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Alert variant="success">
              <CheckCircleIcon className="size-5" />
              <AlertTitle>
                <span className="sr-only">Status:</span>
                You've been scheduled
              </AlertTitle>
              <AlertDescription>
                <p>You have been scheduled for this class session.</p>
              </AlertDescription>
            </Alert>
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            <Button size="sm" data-testid="close-success-modal" aria-label="close" onClick={() => setShowSignupComplete(false)}>
              Close
            </Button>
            <Button size="sm" className="flex items-center space-x-1" onClick={() => downloadSessionDocument(sessionIdForDownload, currentUser.EVUserId)}>
              <PrinterIcon className="h-4 w-4" />
              <span>Print Schedule</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog size="md" open={showDropClassView} onOpenChange={(open) => !open && setShowDropClassView(false)}>
        <DialogContent className="max-w-xl overflow-hidden p-0 gap-0">
          <DialogHeader>
            <DialogTitle>Drop Class Session</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-md text-gray-700">Are you sure you want to remove yourself from this training session?</p>
          </DialogBody>
          <DialogFooter>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowDropClassView(false);
                setShowScheduleView(true);
              }}
            >
              No
            </Button>
            <Button size="sm" onClick={() => dropClassSession(selectedSessionId)}>
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageScheduledClass
        setShowDropClassView={setShowDropClassView}
        setShowScheduleView={setShowScheduleView}
        showScheduleView={showScheduleView}
        scheduledClassId={selectedSessionId}
        fetchClassDetail={getScheduledClassDetailForStaffing}
        fetchPrintDocument={getClassDocumentBySessionIdForStaffing}
        allowDrop={!managingCompletedClass}
      />

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto mt-4 mb-2">
          <h1 className="text-base font-semibold leading-6 text-gray-900">My Training</h1>
          {!loading && hasAnyTraining ? <p className="mt-2 text-sm text-gray-700">{outstandingSubtitle}</p> : null}
        </div>
      </div>

      {!loading && hasAnyTraining ? <SectionJumps groups={sectionJumpGroups} /> : null}

      {loading ? (
        <div className="my-20 flex flex-col items-center justify-center">
          <Flexor className="space-x-3">
            <Spinner />
            <span>Loading training requirements...</span>
          </Flexor>
        </div>
      ) : !hasAnyTraining ? (
        <div>
          <PageEmptyState
            icon={AcademicCapIcon}
            emptyMessageHeader="No training assigned"
            emptyMessageSubHeader="When you have required or extra training, it will show up here."
            reload={reloadTraining}
          />
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <TrainingSection
            id={INCOMPLETE_SECTION_ID}
            title="Incomplete Courses"
            description="Finish these courses before you work."
            icon={ComputerDesktopIcon}
            iconColor="text-indigo-500"
            bgColor="bg-indigo-50"
            borderColor="border-indigo-300"
            count={incompleteCount}
            emptyMessage="No incomplete courses."
          >
            {groupedClasses.incompleteRemote.map(({ trainingClass, session, lms }) => (
              <RemoteCourseRow
                key={trainingClass.Id}
                trainingClass={trainingClass}
                lms={lms}
                onStart={trainingClass.SessionId ? undefined : () => startRemoteSession(session)}
                starting={signingUp === session.Id}
              />
            ))}
          </TrainingSection>

          <TrainingSection
            id={UNSCHEDULED_SECTION_ID}
            title="Unscheduled"
            description="Choose a session or start an online course."
            icon={ClockIcon}
            iconColor="text-orange-500"
            bgColor="bg-orange-50"
            borderColor="border-orange-300"
            count={unscheduledCount}
            emptyMessage="No unscheduled courses."
          >
            {groupedClasses.unscheduledRemote.map(({ trainingClass, session, lms }) => (
              <RemoteCourseRow key={trainingClass.Id} trainingClass={trainingClass} lms={lms} onStart={() => startRemoteSession(session)} starting={signingUp === session.Id} />
            ))}
            {groupedClasses.unscheduled.map((tc) => {
              const hasApplicableSessions = tc.HasApplicableSessions;
              const actionLabel = tc.Status === TrainingClassStatus.NeedsAction ? 'Sign Up' : hasApplicableSessions ? 'View sessions' : undefined;

              return (
                <TrainingClassRow
                  key={tc.Id}
                  trainingClass={tc}
                  onAction={hasApplicableSessions ? () => openSessionPicker(tc) : undefined}
                  actionLabel={actionLabel}
                  hasOnlineOption={hasApplicableSessions && hasOnlineOption(tc, lmsEnabled)}
                />
              );
            })}
          </TrainingSection>

          <TrainingSection
            id={BOOKED_SECTION_ID}
            title="Booked"
            description="You already have a seat in these sessions."
            icon={CalendarDaysIcon}
            iconColor="text-blue-500"
            bgColor="bg-blue-50"
            borderColor="border-blue-300"
            count={groupedClasses.booked.length}
            emptyMessage="No booked sessions."
          >
            {groupedClasses.booked.map((tc) => (
              <TrainingClassRow key={tc.Id} trainingClass={tc} onAction={() => openManageClass(tc.SessionId)} actionLabel="View schedule" />
            ))}
          </TrainingSection>

          <TrainingSection
            id={COMPLETED_SECTION_ID}
            title="Completed"
            icon={CheckCircleIcon}
            iconColor="text-green-500"
            bgColor="bg-green-50"
            borderColor="border-green-300"
            count={completedCount}
            emptyMessage="No completed training."
          >
            {groupedClasses.completedRemote.map(({ trainingClass, lms }) => (
              <RemoteCourseRow key={trainingClass.Id} trainingClass={trainingClass} lms={lms} completed />
            ))}
            {groupedClasses.completedInPerson.map((tc) => (
              <TrainingClassRow
                key={tc.Id}
                trainingClass={tc}
                completed={tc.Status === TrainingClassStatus.Completed}
                cancelled={tc.Status === TrainingClassStatus.Cancelled}
                onAction={tc.SessionId ? () => openManageClass(tc.SessionId, true) : undefined}
                actionLabel={tc.SessionId ? 'View details' : undefined}
                actionVariant="outline"
              />
            ))}
          </TrainingSection>
        </div>
      )}
    </div>
  );
}
